import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface PixelSetup {
  platforms: Array<{
    name: string
    pixelId: string | null
    installed: boolean
    instructions: string
  }>
}

export function AnalyticsPage() {
  const { data, error, isLoading } = useQuery<PixelSetup, ApiClientError>({
    queryKey: ['analytics-pixel'],
    queryFn: () => artistFetch<PixelSetup>('/analytics/pixel-setup'),
  })

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Fan intelligence — pixel setup, geographic heat maps, streaming data, brand affinity."
      />

      <AsyncBoundary isLoading={isLoading} error={error}>
        <div className="space-y-6">
          <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
            <h2 className="mb-4 font-display text-xl">Pixel setup guide</h2>
            {data?.platforms && data.platforms.length > 0 ? (
              <div className="space-y-3">
                {data.platforms.map((p) => (
                  <div
                    key={p.name}
                    className="flex items-start justify-between gap-4 rounded-md border border-ovation-ink/5 bg-ovation-paper/30 p-3"
                  >
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-ovation-muted">
                        {p.instructions}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded px-2 py-0.5 text-xs ${
                        p.installed
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {p.installed ? 'Installed' : 'Not installed'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-ovation-muted">
                No pixel configuration yet. Set up GA4 + Meta Pixel + LinkedIn
                Insight Tag on the artist's website to start tracking fan
                engagement.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
              <h3 className="mb-3 font-display text-lg">Fan geographic heat map</h3>
              <div className="text-sm text-ovation-muted">
                Geographic fan density from combined first-party (pixels + ticket
                purchases) and streaming data (Spotify + Chartmetric). Requires
                pixel install + Spotify/Chartmetric integration keys.
              </div>
            </div>
            <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
              <h3 className="mb-3 font-display text-lg">Streaming trends</h3>
              <div className="text-sm text-ovation-muted">
                Rolling time windows (7d, 30d, 90d, 6m, 12m) from Spotify for
                Artists + Chartmetric. Shows monthly listener count, top cities,
                related artists. Requires SPOTIFY_CLIENT_ID + CHARTMETRIC_API_KEY.
              </div>
            </div>
            <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
              <h3 className="mb-3 font-display text-lg">Brand affinity</h3>
              <div className="text-sm text-ovation-muted">
                Audience overlap signals for sponsorship targeting. Requires
                Meta Pixel + LinkedIn Insight data to be flowing.
              </div>
            </div>
            <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
              <h3 className="mb-3 font-display text-lg">Fan polling</h3>
              <div className="text-sm text-ovation-muted">
                GDPR-compliant demand polling with geographic aggregation.
                Responses anonymised immediately — no PII in analytics tables.
              </div>
            </div>
          </div>
        </div>
      </AsyncBoundary>
    </div>
  )
}
